// third party imports
import React, { useState, useEffect, useContext, useRef } from "react";

// internal rebet imports
import "./stylesManualEntries.css";
import { KPI_CARD_BACKGROUND, SIDER_TABS } from "../../utils/Constants";
import Layout from "../CommonComponents/Layout/Layout";

// component imports
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import AppContext from "../../context/AppContext";
import { Popover, Select, Table, Progress, Badge } from "antd";
import CustomDatePicker from "../CommonComponents/CustomDatePicker/CustomDatePicker";
import dayjs from "dayjs";
import KpiCard from "../CommonComponents/Dashboard/KpiCard/KpiCard";
import SaveButton from "../CommonComponents/SaveButton/SaveButton";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DownloadOutlined } from '@ant-design/icons';


const ManualEntries = () => {
  // non-state variables
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const dateRange = [dayjs().startOf("month"), dayjs()];

  const [manualEntriesDates, setManualEntriesDates] = useState(dateRange);

  const { setActiveSidebarTab } = useContext(AppContext);

  // state variables
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [formData, setFormData] = useState({
    movement: "credited",
    supportAgent: null,
    manager: null,
  });

  const [manualEntriesPaginatedData, setManualEntriesPaginatedData] = useState(
    []
  );

  const [overallData, setOverallData] = useState({
    totalValue: 0,
    totalReBetCashAdded: 0,
    totalReBetCashSubtracted: 0,
    totalReBetFreeCashAdded: 0,
  });

  const [supportAgents, setSupportAgents] = useState([]);
  const [managers, setManagers] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    currentPage: 0,
    totalPages: 0,
    itemsCollected: 0,
  });
  const [showProgressBar, setShowProgressBar] = useState(false);
  const downloadButtonRef = useRef(null);
  const progressBarRef = useRef(null);

  const getSupportAgentById = (id) => {
    const supportAgent = supportAgents.find((agent) => agent.admin_id === id);
    if (supportAgent?.admin_name === "No Support Agent") {
      return "-";
    }
    return supportAgent;
  };

  const shouldHideManualEntry = (entry) => {
    const normalizedAgent =
      typeof entry?.agent === "string"
        ? entry.agent.trim().toLowerCase()
        : "";
    const normalizedDescription =
      typeof entry?.description === "string"
        ? entry.description.trim().toLowerCase()
        : "";

    return normalizedAgent === "tbd" && normalizedDescription === "cash out";
  };

  const filterManualEntries = (items = []) =>
    items.filter((entry) => !shouldHideManualEntry(entry));

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 100,
      sorter: (a, b) => a.location.localeCompare(b.location),
      render: (text, record) => {
        return record?.location || "-";
      },
    },
    {
      title: "Date / Time",
      dataIndex: "date_time",
      key: "date_time",
      width: 250,
      sorter: (a, b) => a.date_time.localeCompare(b.date_time),
      render: (text, record) => {
        return record?.date_time || "-";
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      sorter: (a, b) => a.description.localeCompare(b.description),
      render: (text, record) => {
        return record?.description ? (
          <Popover content={record?.description} placement="topLeft"
          overlayInnerStyle={{ maxWidth: 300 }}>
            <p
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
            >
              {record?.description}
            </p>
          </Popover>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      title: "Entry Type",
      dataIndex: "entry_type",
      key: "entry_type",
      sorter: (a, b) => a.entry_type.localeCompare(b.entry_type),
      render: (text, record) => {
        return record?.entry_type || "-";
      },
    },
    {
      title: "Manager",
      dataIndex: "manager",
      key: "manager",
      sorter: (a, b) => a.manager.localeCompare(b.manager),
      render: (text, record) => {
        return record?.manager || "-";
      },
    },
    {
      title: "Agent",
      dataIndex: "agent",
      key: "agent",
      width: 250,
      sorter: (a, b) => a.agent.localeCompare(b.agent),
      render: (text, record) => {
        return record?.agent || "-";
      },
    },
    {
      title: "User",
      dataIndex: "user_email",
      key: "user_email",
      sorter: (a, b) => a.user_email.localeCompare(b.user_email),
      render: (text, record) => {
        return record?.user_email || "-";
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.amount.localeCompare(b.amount),
      render: (text, record) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(record?.amount || 0);
      },
    },
  ];

  // use effects
  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.STAFF_MANAGEMENT, "Manual Entries");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    getManualEntries();
  }, []);

  // Handle clicks outside progress bar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        progressBarRef.current &&
        !progressBarRef.current.contains(event.target) &&
        downloadButtonRef.current &&
        !downloadButtonRef.current.contains(event.target)
      ) {
        setShowProgressBar(false);
      }
    };

    if (showProgressBar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProgressBar]);

  const loadMoreManualEntries = async () => {
    const params = {
      start_date: manualEntriesDates[0].format("YYYY-MM-DD"),
      end_date: manualEntriesDates[1].format("YYYY-MM-DD"),
      page: pagination.current_page + 1,
      page_size: pagination.page_size,
      support_agent: formData.supportAgent,
      manager: formData.manager,
      transaction_type: formData.movement,
    };


    setIsApiRunning(true);
    try {
      const requests = [
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_MANUAL_ENTRIES_PAGINATED,
          params
        ),
      ];

      const responses = await Promise.allSettled(requests);

      const [manualEntriesPaginated] = responses;

      if (manualEntriesPaginated.status === "fulfilled") {
        const manualEntriesPaginatedData =
          manualEntriesPaginated?.value?.data?.data || [];
        setManualEntriesPaginatedData((prev) => [
          ...prev,
          ...filterManualEntries(manualEntriesPaginatedData.items),
        ]);
        setPagination(manualEntriesPaginatedData.pagination || {});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsApiRunning(false);
    }
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    setShowProgressBar(true);
    setExportProgress({ currentPage: 0, totalPages: 0, itemsCollected: 0 });

    try {
      let allData = [];
      let currentPage = 1;
      let hasMoreData = true;
      let totalPages = 0;

      // Fetch all pages of data
      while (hasMoreData) {

        const response = await PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_MANUAL_ENTRIES_PAGINATED,
          {
            start_date: manualEntriesDates[0].format("YYYY-MM-DD"),
            end_date: manualEntriesDates[1].format("YYYY-MM-DD"),
            page: currentPage,
            page_size: 100, // Reasonable page size
            support_agent: formData.supportAgent,
            manager: formData.manager,
            transaction_type: formData.movement,
          }
        );


        const pageData = response?.data?.data?.items || [];
        const paginationInfo = response?.data?.data?.pagination || {};
        // Update total pages on first request
        if (currentPage === 1) {
          totalPages = paginationInfo.total_pages || 1;
        }
        // Filter out rows where BOTH Location AND Agent are "TBD" in real-time
        const filteredPageData = pageData.filter((entry) => {
          const location = entry?.location || "";
          const agent = entry?.agent || "";

          // Exclude if BOTH are "TBD"
          if (location === "TBD" && agent === "TBD") {
            return false;
          }
          return true;
        });


        allData = [...allData, ...filteredPageData];

        // Update progress with filtered count
        setExportProgress({
          currentPage: currentPage,
          totalPages: totalPages,
          itemsCollected: allData.length,
        });

        // Check if there are more pages
        hasMoreData = paginationInfo.has_next || false;
        currentPage++;
      }


      if (!allData || allData.length === 0) {
        return;
      }


      // Format data for export
      const exportingData = allData.map((entry) => ({
        Location: entry?.location || "-",
        "Date / Time": entry?.date_time || "-",
        Description: entry?.description || "-",
        "Entry Type": entry?.entry_type || "-",
        Manager: entry?.manager || "-",
        Agent: entry?.agent || "-",
        User: entry?.user_email || "-",
        Amount: entry?.amount || "-",
      }));


      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(exportingData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Manual Entries');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const fileData = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });

      saveAs(fileData, 'manual-entries.xlsx');
    } catch (error) {
      console.error("Full error:", error);
    } finally {
      setIsExporting(false);
      setShowProgressBar(false);
      setExportProgress({ currentPage: 0, totalPages: 0, itemsCollected: 0 });
    }
  };

  const getManualEntries = async () => {
    setIsApiRunning(true);

    const params = {
      start_date: manualEntriesDates[0].format("YYYY-MM-DD"),
      end_date: manualEntriesDates[1].format("YYYY-MM-DD"),
      support_agent: formData.supportAgent,
      manager: formData.manager,
      transaction_type: formData.movement,
      page: 1,
      page_size: 10,
    };

    try {
      const requests = [
        PrivateAdminPanelInstance?.post(API_ROUTES?.GET_MANAGERS),
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_MANUAL_ENTRIES_ANALYTICS,
          {
            start_date: manualEntriesDates[0].format("YYYY-MM-DD"),
            end_date: manualEntriesDates[1].format("YYYY-MM-DD"),
          }
        ),
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_MANUAL_ENTRIES_PAGINATED,
          {
            start_date: manualEntriesDates[0].format("YYYY-MM-DD"),
            end_date: manualEntriesDates[1].format("YYYY-MM-DD"),
            page: 1,
            page_size: 10,
            support_agent: formData.supportAgent,
            manager: formData.manager,
            transaction_type: formData.movement,
          }
        ),
        PrivateAdminPanelInstance?.post(API_ROUTES?.GET_SUPPORT_AGENTS),
      ];

      const responses = await Promise.allSettled(requests);

      const [
        managers,
        manualEntriesAnalytics,
        manualEntriesPaginated,
        supportAgents,
      ] = responses;

      if (managers.status === "fulfilled") {
        const managersData = managers?.value?.data?.data || [];
        const formattedManagersData = managersData.map((manager) => ({
          admin_id: manager.admin_id,
          admin_name: manager.admin_name,
          value: manager.admin_id,
          label: manager.admin_name,
        }));
        setManagers([
          {
            admin_id: null,
            admin_name: "No Manager",
            value: null,
            label: "No Manager",
          },
          ...formattedManagersData,
        ]);
      }


      if (manualEntriesPaginated.status === "fulfilled") {
        const manualEntriesPaginatedData =
          manualEntriesPaginated?.value?.data?.data || [];
        setOverallData({
          totalValue: manualEntriesPaginatedData.aggregates?.overall_totals?.total_credited || 0,
          totalReBetCashAdded:
            manualEntriesPaginatedData.aggregates?.rebet_cash?.added || 0,
          totalReBetCashSubtracted:
            manualEntriesPaginatedData.aggregates?.rebet_cash?.subtracted || 0,
          totalReBetFreeCashAdded:
            manualEntriesPaginatedData.aggregates?.rebet_free_cash?.added || 0,
        });
        setManualEntriesPaginatedData(
          filterManualEntries(manualEntriesPaginatedData.items)
        );
        setPagination(manualEntriesPaginatedData.pagination || {});
      }

      if (supportAgents.status === "fulfilled") {
        const supportAgentsData = supportAgents?.value?.data?.data || [];
        const formattedSupportAgentsData = supportAgentsData.map((agent) => ({
          admin_id: agent.admin_id,
          admin_name: agent.admin_name,
          value: agent.admin_id,
          label: agent.admin_name,
        }));

        const sortedSupportAgentsData = formattedSupportAgentsData.sort(
          (a, b) => a.admin_name.localeCompare(b.admin_name)
        );

        setSupportAgents(
          [
            {
              admin_id: null,
              admin_name: "No Support Agent",
              value: null,
              label: "No Support Agent",
            },
            ...sortedSupportAgentsData,
          ] || []
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsApiRunning(false);
    }
  };

  return (
    <Layout name={SIDER_TABS.STAFF_MANAGEMENT}>
      <div className="manual-entries-container">
        <div className="manual-entries-header-title-container">
          <h2>Manual Entries</h2>
          <div className={'date-range-picker-export-button-container'}>
            <div className="manual-entries-custom-datepicker">
            <CustomDatePicker
                getData={() => {}}
                dates={manualEntriesDates}
                setDates={setManualEntriesDates}
                showQuickSelect={true}
                showPresets={true}
            />
          </div>
            <div className="export-to-xlsx-button-container" style={{ position: 'relative' }}>
            <Badge dot={isExporting} color="#108ee9">
              <button
                  ref={downloadButtonRef}
                  onClick={() => {
                    if (isExporting) {
                      setShowProgressBar(!showProgressBar);
                    } else {
                      handleExportToExcel();
                    }
                  }}
                  className={`export-to-xlsx-button ${
                      manualEntriesPaginatedData.length === 0 ? 'disabled' : ''
                  }`}
                  disabled={manualEntriesPaginatedData.length === 0}
              >
                <DownloadOutlined className="download-icon" />
              </button>
            </Badge>

            {/* Floating Export Progress Bar */}
            {isExporting && showProgressBar && (
                <div
                    ref={progressBarRef}
                    style={{
                      position: 'absolute',
                      top: '50px',
                      right: '0',
                      width: '350px',
                      padding: '12px',
                      backgroundColor: 'var(--grey-750)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      zIndex: 1000,
                      border: '1px solid var(--grey-600)'
                    }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '15px',
                    width: '0',
                    height: '0',
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderBottom: '8px solid var(--grey-750)'
                  }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Progress
                        percent={
                          exportProgress.totalPages > 0
                              ? Math.round((exportProgress.currentPage / exportProgress.totalPages) * 100)
                              : 0
                        }
                        status="active"
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        size="small"
                        format={(percent) => (<span style={{ color: 'var(--grey-300)', fontSize: '12px' }}>{percent}%</span>)}
                    />
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      color: 'var(--grey-300)',
                      fontSize: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Page:</span>
                        <span>{exportProgress.currentPage} of {exportProgress.totalPages}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Collected Items:</span>
                        <span>{exportProgress.itemsCollected}</span>
                      </div>
                    </div>
                  </div>
                </div>
            )}
          </div>
          </div>

        </div>
        <div className="manual-entries-header">
          <div className="manual-entries-header-selects-container">
            <div className="manual-entries-header-selects-container-left">
              {/* <div className="manual-entries-header-select">
                <span>Location</span>
                <Select
                  // value={formData.card}
                  // onChange={(value) => handleFieldChange("card", value)}
                  options={[
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                  ]}
                />
              </div> */}
              <div className="manual-entries-header-select">
                <span>Movement</span>
                <Select
                  value={formData.movement}
                  onChange={(value) =>
                    setFormData({ ...formData, movement: value })
                  }
                  options={[
                    { value: "credited", label: "ReBet Cash Added" },
                    {
                      value: "debited",
                      label: "ReBet Cash Subtracted",
                    },
                  ]}
                />
              </div>
              <div className="manual-entries-header-select">
                <span>Support Agent</span>
                <Select
                  value={formData.supportAgent}
                  onChange={(value) =>
                    setFormData({ ...formData, supportAgent: value })
                  }
                  options={supportAgents}
                />
              </div>
              <div className="manual-entries-header-select">
                <span>Manager</span>
                <Select
                  value={formData.manager}
                  onChange={(value) =>
                    setFormData({ ...formData, manager: value })
                  }
                  options={managers}
                />
              </div>
            </div>
            <div className="manual-entries-header-selects-container-right"></div>
          </div>
          <div className="manual-entries-header-action-buttons-container">
            <SaveButton
              isApiRunning={isApiRunning}
              callbackFunction={getManualEntries}
              buttonText="Apply"
            />
          </div>
        </div>

        <div className="manual-entries-body">
          <div className="manual-entries-kpi-cards-container">
            <KpiCard
              isApiRunning={isApiRunning}
              title="Total Value"
              value={overallData.totalValue}
              hugeValue={false}
              percentage={false}
              backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
            />
            <KpiCard
              isApiRunning={isApiRunning}
              title="Total ReBet Cash Added"
              value={overallData.totalReBetCashAdded}
              hugeValue={false}
              percentage={false}
              backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
            />
            <KpiCard
              isApiRunning={isApiRunning}
              title="Total ReBet Cash Subtracted"
              value={overallData.totalReBetCashSubtracted}
              hugeValue={false}
              percentage={false}
              backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
            />
          </div>
          <div className="manual-entries-kpi-cards-container">
            <KpiCard
              isApiRunning={isApiRunning}
              title="Total ReBet Free Cash Added"
              value={overallData.totalReBetFreeCashAdded}
              hugeValue={false}
              percentage={false}
              backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
            />
          </div>
          <div className="manual-entries-table-container">
            <Table
              columns={columns}
              dataSource={manualEntriesPaginatedData}
              scroll={{ x: 1500, y: 500 }}
              pagination={false}
            />

            {pagination.has_next && (
              <div className="manual-entries-table-load-more-button-container">
                <SaveButton
                  buttonText="Load More"
                  callbackFunction={loadMoreManualEntries}
                  isLoading={isApiRunning}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManualEntries;
